import { A } from '@solidjs/router';
import { createMemo, createSignal, For, Show, type JSX } from 'solid-js';
import { BoxCard } from '../components/BoxCard';
import { DeployStackDialog } from '../components/DeployStackDialog';
import { DestroyBoxDialog } from '../components/DestroyBoxDialog';
import { EmptyState, ErrorAlert, LoadingRows } from '../components/states';
import { flatten, useBoxes } from '../lib/queries';
import type { BoxListItem } from '../lib/types';
import { Button, buttonClass } from '../ui/Button';
import { Icon } from '../ui/Icon';

export function Boxes(): JSX.Element {
  const boxes = useBoxes();
  const items = createMemo(() => flatten(boxes.data?.pages));
  const [deployTarget, setDeployTarget] = createSignal<BoxListItem>();
  const [destroyTarget, setDestroyTarget] = createSignal<BoxListItem>();

  const updating = createMemo(() => items().filter((box) => box.box_status === 'Updating').length);

  return (
    <main class="page">
      <div class="page__head">
        <div class="page__title">
          <h1>Boxes</h1>
          <span class="page__subtitle">
            <Show when={updating() > 0} fallback="Your development machines.">
              {updating()} box{updating() === 1 ? '' : 'es'} still working…
            </Show>
          </span>
        </div>
        <div class="page__actions">
          <Button variant="secondary" onClick={() => void boxes.refetch()} loading={boxes.isFetching}>
            <Icon name="refresh" />
            Refresh
          </Button>
          <A href="/boxes/new" class={buttonClass('primary', 'md')}>
            <Icon name="plus" />
            New box
          </A>
        </div>
      </div>

      <Show when={boxes.isError}>
        <ErrorAlert title="Could not load boxes" error={boxes.error} onRetry={() => void boxes.refetch()} />
      </Show>

      <Show when={!boxes.isPending} fallback={<LoadingRows rows={3} height={220} />}>
        <Show
          when={items().length > 0}
          fallback={
            <EmptyState
              icon="box"
              title="No boxes yet"
              text="Create a box to get a machine ready for running your AI tooling — deploy stacks like opencode, pi.dev, or MCP servers onto it in a few clicks."
              action={
                <A href="/boxes/new" class={buttonClass('primary', 'md')}>
                  <Icon name="plus" />
                  Create your first box
                </A>
              }
            />
          }
        >
          <div class="box-grid">
            <For each={items()}>
              {(box) => <BoxCard box={box} onDeploy={setDeployTarget} onDestroy={setDestroyTarget} />}
            </For>
          </div>
        </Show>
      </Show>

      <Show when={boxes.hasNextPage}>
        <div class="row gap-3" style={{ 'justify-content': 'center', 'margin-top': 'var(--space-6)' }}>
          <Button variant="secondary" loading={boxes.isFetchingNextPage} onClick={() => void boxes.fetchNextPage()}>
            Load more
          </Button>
        </div>
      </Show>

      <DeployStackDialog
        open={deployTarget() !== undefined}
        box={deployTarget()}
        onClose={() => setDeployTarget(undefined)}
      />
      <DestroyBoxDialog box={destroyTarget()} onClose={() => setDestroyTarget(undefined)} />
    </main>
  );
}
